import React, { useState , useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import styled, { createGlobalStyle } from 'styled-components';
import { FaLock, FaEnvelope, FaSignInAlt } from 'react-icons/fa';

const Login = () => {

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');

    const { login, loading, error, user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (user) {
            navigate('/applications', { replace: true });
        }
    }, [user, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const result = await login(email, password);
        if (result.success) {
            navigate('/applications', { replace: true });
        }
    };

    return (
        <>
            <GlobalStyles />
            <LoginContainer>
                <LoginBox>
                    <LoginHeader>
                        <LoginTitle>Welcome Back</LoginTitle>
                        <LoginSubtitle>Please enter your credentials to access the admin dashboard</LoginSubtitle>
                    </LoginHeader>

                    {error && (
                        <ErrorMessage>
                            <i className="bi bi-exclamation-circle-fill"></i>
                            {error}
                        </ErrorMessage>
                    )}

                    <LoginForm onSubmit={handleSubmit}>
                        <FormGroup>
                            <InputLabel htmlFor="email">Email Address</InputLabel>
                            <InputContainer>
                                <IconWrapper>
                                    <FaEnvelope />
                                </IconWrapper>
                                <StyledInput
                                    id="email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="admin@example.com"
                                    required
                                    autoFocus
                                />
                            </InputContainer>
                        </FormGroup>

                        <FormGroup>
                            <InputLabel htmlFor="password">Password</InputLabel>
                            <InputContainer>
                                <IconWrapper>
                                    <FaLock />
                                </IconWrapper>
                                <StyledInput
                                    id="password"
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••"
                                    required
                                />
                            </InputContainer>
                        </FormGroup>

                        <SubmitButton type="submit" disabled={loading}>
                            {loading ? (
                                <Spinner />
                            ) : (
                                <>
                                    <FaSignInAlt className="me-2" />
                                    Login
                                </>
                            )}
                        </SubmitButton>
                    </LoginForm>
                </LoginBox>
            </LoginContainer>
        </>
    );
};

// Global styles to prevent scrolling
const GlobalStyles = createGlobalStyle`
    html, body {
        height: 100%;
        overflow: hidden;
        margin: 0;
        padding: 0;
    }
`;

// Styled Components
const LoginContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh;
    width: 100vw;
    background: linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%);
    position: fixed;
    top: 0;
    left: 0;
`;

const LoginBox = styled.div`
    background-color: #fff;
    border-radius: 12px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
    width: 100%;
    max-width: 420px;
    overflow: hidden;
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    margin: 20px;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 15px 35px rgba(0, 0, 0, 0.12);
    }
`;

const LoginHeader = styled.div`
    background: linear-gradient(135deg, #18697C 0%, #143e47 100%);
    color: white;
    padding: 2.5rem 2rem;
    text-align: center;
`;

const LoginTitle = styled.h2`
    font-size: 1.5rem;
    font-weight: 600;
    margin: 0.5rem 0;
`;

const LoginSubtitle = styled.p`
    font-size: 0.9rem;
    opacity: 0.9;
    margin: 0;
`;

const LoginForm = styled.form`
    padding: 2rem;
`;

const FormGroup = styled.div`
    margin-bottom: 1.5rem;
`;

const InputLabel = styled.label`
    display: block;
    margin-bottom: 0.5rem;
    font-size: 0.9rem;
    font-weight: 500;
    color: #4a5568;
`;

const InputContainer = styled.div`
    position: relative;
    display: flex;
    align-items: center;
`;

const IconWrapper = styled.div`
    position: absolute;
    left: 1rem;
    color: #718096;
    font-size: 1rem;
`;

const StyledInput = styled.input`
    width: 100%;
    padding: 0.75rem 1rem 0.75rem 2.5rem;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    font-size: 0.95rem;
    transition: all 0.3s ease;
    background-color: #f8fafc;

    &:focus {
        border-color: #18697C;
        box-shadow: 0 0 0 3px rgba(24, 105, 124, 0.2);
        outline: none;
        background-color: #fff;
    }

    &::placeholder {
        color: #a0aec0;
    }
`;

const ErrorMessage = styled.div`
    background-color: #fff5f5;
    color: #e53e3e;
    padding: 0.75rem 1rem;
    border-radius: 8px;
    margin-bottom: 1.5rem;
    font-size: 0.9rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    border-left: 4px solid #e53e3e;
`;

const SubmitButton = styled.button`
    width: 100%;
    background: linear-gradient(135deg, #18697C 0%, #143e47 100%);
    color: white;
    padding: 0.75rem;
    font-size: 1rem;
    font-weight: 500;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 1.5rem 0;

    &:hover {
        background: linear-gradient(135deg, #143e47 0%, #18697C 100%);
        transform: translateY(-1px);
    }

    &:disabled {
        opacity: 0.7;
        cursor: not-allowed;
        transform: none;
    }
`;

const ForgotPasswordLink = styled.a`
    display: block;
    text-align: center;
    color: #718096;
    font-size: 0.85rem;
    text-decoration: none;
    transition: color 0.2s ease;

    &:hover {
        color: #18697C;
        text-decoration: underline;
    }
`;

const Spinner = styled.div`
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    border-top: 2px solid white;
    width: 20px;
    height: 20px;
    animation: spin 1s linear infinite;

    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }
`;

export default Login;